import { handleUnauthorizedSession, SessionExpiredError } from '../../../api';
import { AnswersEndpoints, answersFetch } from './answersApiClient';
import { buildConversationHistory } from './answersConversationService';

const isAuthFailureDetail = (detail) => (
  typeof detail === 'string'
  && /Authorization header required|Invalid or expired token/i.test(detail)
);

/**
 * Execute a streaming (or JSON fallback) RAG query and apply SSE events via updateTurn.
 */
export async function executeRagQuery({
  question,
  conversation,
  buildFilterPayload,
  updateTurn,
  turnId,
}) {
  const history = buildConversationHistory(conversation);
  const body = {
    question,
    stream: true,
    ...(history?.length ? { history } : {}),
    ...buildFilterPayload(),
  };

  const res = await answersFetch(AnswersEndpoints.QUERY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = err.detail || res.statusText || `Request failed: ${res.status}`;
    if (res.status === 401 || isAuthFailureDetail(detail)) {
      handleUnauthorizedSession();
      throw new SessionExpiredError();
    }
    throw new Error(detail);
  }

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await res.json();
    updateTurn(turnId, (t) => ({
      ...t,
      response: {
        answer: data.answer || '',
        sources: data.sources || [],
        query: data.query || question,
        response_time_ms: data.response_time_ms,
        suggested_questions: data.suggested_questions,
        filter_stats: data.filter_stats,
      },
      streamingAnswer: undefined,
    }));
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('Response stream not accessible');

  const decoder = new TextDecoder();
  let buffer = '';
  let eventType = '';
  let dataLines = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
        dataLines = [];
      } else if (line.startsWith('data: ')) {
        dataLines.push(line.slice(6));
      } else if (line === '' && eventType && dataLines.length) {
        const payload = JSON.parse(dataLines.join(''));
        const typeSnapshot = eventType;
        if (typeSnapshot === 'error') {
          const detail = payload.detail || 'Unknown error';
          if (isAuthFailureDetail(detail)) {
            handleUnauthorizedSession();
            throw new SessionExpiredError();
          }
          throw new Error(detail);
        }
        updateTurn(turnId, (t) => {
          if (typeSnapshot === 'thinking') return { ...t, thinking: `${t.thinking || ''}${payload.content || ''}` };
          if (typeSnapshot === 'token') return { ...t, streamingAnswer: `${t.streamingAnswer || ''}${payload.token || ''}` };
          if (typeSnapshot === 'sources') {
            const sources = payload || [];
            return {
              ...t,
              response: { ...(t.response || { answer: '', query: question }), sources },
            };
          }
          if (typeSnapshot === 'sources_chunk') {
            const existing = t.response?.sources || [];
            const sources = [...existing, ...(payload || [])];
            return {
              ...t,
              response: { ...(t.response || { answer: '', query: question }), sources },
            };
          }
          if (typeSnapshot === 'final') {
            const existingSources = t.response?.sources || [];
            const sources = existingSources.length ? existingSources : (payload.sources || []);
            return {
              ...t,
              response: {
                answer: payload.answer || t.streamingAnswer || '',
                sources,
                query: payload.query || question,
                response_time_ms: payload.response_time_ms,
                suggested_questions: payload.suggested_questions,
                filter_stats: payload.filter_stats,
              },
              streamingAnswer: undefined,
            };
          }
          return t;
        });
        eventType = '';
        dataLines = [];
      }
    }
  }
}
